import { useMemo } from 'react';
import { useQuery, QueryHookOptions, QueryResult } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { INodeData } from './NodeQuery';
import { NewId, NO_ID_AVAILABLE } from '../../MasterDetail/MasterDetailUtils';

/** Svar på en node fråga */
interface INodeResultData {
  node: INodeData | null;
}

export interface INodeQueryResult<TData, TNodeData, TVariables>
  extends QueryResult<TData, TVariables> {
  nodeData: TNodeData | null | undefined;
}
const useNodeQuery: <
  TData extends INodeResultData,
  TNodeData extends INodeData,
  TVariables extends { id: string }
>(
  query: DocumentNode,
  nodeId: string | null,
  options?: QueryHookOptions<TData, TVariables>
) => INodeQueryResult<TData, TNodeData, TVariables> = (
  query,
  nodeId,
  options
) => {
  const isNew = nodeId === NewId;
  const id = isNew ? '' : nodeId;
  const newOptions = useMemo(
    () => ({
      skip: id == null || id === '' || id === NO_ID_AVAILABLE,
      variables: { id: id ?? '' },
      ...(options || {}),
    }),
    [id, options]
  );
  const useQueryResult = useQuery(
    query,
    newOptions as QueryHookOptions<any, any>
  );
  const nodeData = useQueryResult.data && useQueryResult.data.node;
  const result = useMemo(
    () => ({ nodeData, ...useQueryResult }),
    [nodeData, useQueryResult]
  );
  const empty: any = { loading: false };
  return isNew ? empty : result;
};

export default useNodeQuery;
